// MeasurementCalculatorSheet — bottom-sheet area/volume calculator for
// m² (dimensions=2) and m³ (dimensions=3) capture rows. Surveyors enter
// each dimension on their tape measure, the sheet computes the product
// rounded to 2 decimals, and writes the canonical decimal string back
// to the parent on Apply.
//
// currentValue is shown as "Currently: x unit" so a re-open after a
// previous capture still surfaces the stored value — the calculator
// opens with empty dimensions because we don't reverse-decompose a
// stored area into dimensions.
//
// A11y mirrors NumericKeypadSheet: role=dialog, aria-modal, body inert
// while open, Esc closes, overlay closes, Tab traps within the sheet.
//
// The sheet body (overlay + dialog markup + keypad grid) lives in
// `./measurement-calculator-sheet-content` to keep this file under
// the project's max-lines policy.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import { createPortal } from 'react-dom'

import { MeasurementCalculatorSheetContent } from './measurement-calculator-sheet-content'

export interface MeasurementCalculatorSheetProps {
  open: boolean
  onClose: () => void
  /** 2 for m² (L × W), 3 for m³ (L × W × H). */
  dimensions: 2 | 3
  /** Unit suffix shown next to the result preview ("m²", "m³"). */
  unit: string
  /** Stored canonical decimal-string value, "" if unset. Shown as
   *  "Currently: x unit" so the user knows what's already saved. */
  currentValue: string
  /** Apply the computed product back to the parent. Called with the
   *  decimal string ("" if any dimension is missing or non-positive). */
  onApply: (result: string) => void
  /** Optional heading shown above the dimension row. */
  title?: string
  /** Accessible label for the dialog when no title is provided. */
  ariaLabel: string
  /** Max digits after the decimal point for each dimension. Default 2. */
  maxDecimals?: number
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function getFocusableWithin(root: HTMLElement): HTMLElement[] {
  const nodes = root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
  return Array.from(nodes).filter((el) => !el.hasAttribute('disabled'))
}

function canAppendDigit(current: string, maxDecimals: number): boolean {
  const dotIdx = current.indexOf('.')
  if (dotIdx === -1) return true
  const decimals = current.length - dotIdx - 1
  return decimals < maxDecimals
}

const DIMENSION_LABELS_BY_COUNT: Record<2 | 3, readonly string[]> = {
  2: ['Length', 'Width'],
  3: ['Length', 'Width', 'Height'],
}

function parseDimension(value: string): number {
  if (value === '' || value === '.') return Number.NaN
  const n = Number.parseFloat(value)
  return Number.isFinite(n) ? n : Number.NaN
}

function computeProduct(dims: readonly string[]): string {
  let product = 1
  for (const d of dims) {
    const parsed = parseDimension(d)
    if (!Number.isFinite(parsed) || parsed <= 0) return ''
    product *= parsed
  }
  return product.toFixed(2)
}

function previewDimension(value: string): string {
  return value === '' ? '—' : value
}

export function MeasurementCalculatorSheet(
  props: MeasurementCalculatorSheetProps,
) {
  // Conditionally mount the inner component on `open` so its useState
  // initializers run fresh each time the sheet opens — gives us the
  // "reset dimensions on re-open" semantics without an in-effect
  // setState (banned by react-hooks/set-state-in-effect) or a render-
  // phase ref write (banned by react-hooks/refs). We deliberately do
  // NOT reverse-decompose `currentValue` into dimensions; the surveyor
  // either re-measures (overwrites) or cancels (Esc / overlay) to keep
  // the existing value, which is shown in the "Currently:" line.
  if (!props.open) return null
  return <MeasurementCalculatorSheetInner {...props} />
}

function MeasurementCalculatorSheetInner({
  onClose,
  dimensions,
  unit,
  currentValue,
  onApply,
  title,
  ariaLabel,
  maxDecimals = 2,
}: Omit<MeasurementCalculatorSheetProps, 'open'>) {
  const sheetRef = useRef<HTMLDivElement | null>(null)
  const invokerRef = useRef<HTMLElement | null>(null)
  const [portalHost] = useState<HTMLDivElement | null>(() => {
    if (typeof document === 'undefined') return null
    const host = document.createElement('div')
    host.setAttribute('data-measurement-calculator-root', '')
    return host
  })

  const [dims, setDims] = useState<string[]>(() =>
    Array<string>(dimensions).fill(''),
  )
  const [activeIdx, setActiveIdx] = useState(0)

  const updateActive = useCallback(
    (next: string) => {
      setDims((prev) => {
        const copy = prev.slice()
        copy[activeIdx] = next
        return copy
      })
    },
    [activeIdx],
  )

  const appendDigit = useCallback(
    (digit: string) => {
      const current = dims[activeIdx] ?? ''
      if (!canAppendDigit(current, maxDecimals)) return
      updateActive(current + digit)
    },
    [activeIdx, dims, maxDecimals, updateActive],
  )

  const appendDecimal = useCallback(() => {
    const current = dims[activeIdx] ?? ''
    if (current.includes('.')) return
    updateActive(current === '' ? '0.' : current + '.')
  }, [activeIdx, dims, updateActive])

  const backspace = useCallback(() => {
    const current = dims[activeIdx] ?? ''
    if (current === '') return
    updateActive(current.slice(0, -1))
  }, [activeIdx, dims, updateActive])

  // Bug #8: Clear resets every dimension and refocuses the first box
  // (previously cleared only the active one, leaving stale values on
  // the others — confusing when a surveyor mistyped both numbers).
  const clearAll = useCallback(() => {
    setDims((prev) => {
      if (prev.every((d) => d === '')) return prev
      return Array<string>(dimensions).fill('')
    })
    setActiveIdx(0)
  }, [dimensions])

  const result = computeProduct(dims)
  const canApply = result !== ''

  const handleApply = useCallback(() => {
    if (!canApply) return
    onApply(result)
    onClose()
  }, [canApply, onApply, onClose, result])

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  // Portal host mount / unmount — runs on every mount of the inner
  // component, which corresponds to each open transition because the
  // outer wrapper conditionally renders this inner only while open.
  useEffect(() => {
    const host = portalHost
    if (host === null) return undefined
    if (!document.body.contains(host)) {
      document.body.appendChild(host)
    }
    return () => {
      if (document.body.contains(host)) {
        document.body.removeChild(host)
      }
    }
  }, [portalHost])

  // Focus + sibling isolation — mirrors Dialog / NumericKeypadSheet so AT
  // users get the same experience across primitives.
  useEffect(() => {
    const previouslyFocused = document.activeElement
    invokerRef.current =
      previouslyFocused instanceof HTMLElement ? previouslyFocused : null
    const root = portalHost
    const sheet = sheetRef.current
    if (root === null || sheet === null) return undefined
    type SiblingSnapshot = {
      el: HTMLElement
      hadInert: boolean
      prevAriaHidden: string | null
    }
    const touched: SiblingSnapshot[] = []
    for (const child of Array.from(document.body.children)) {
      if (child === root) continue
      if (!(child instanceof HTMLElement)) continue
      touched.push({
        el: child,
        hadInert: child.hasAttribute('inert'),
        prevAriaHidden: child.getAttribute('aria-hidden'),
      })
      child.setAttribute('inert', '')
      child.setAttribute('aria-hidden', 'true')
    }
    const focusables = getFocusableWithin(sheet)
    const firstFocusable = focusables[0]
    if (firstFocusable !== undefined) {
      firstFocusable.focus()
    } else {
      sheet.focus()
    }
    return () => {
      for (const snap of touched) {
        if (!snap.hadInert) snap.el.removeAttribute('inert')
        if (snap.prevAriaHidden === null) {
          snap.el.removeAttribute('aria-hidden')
        } else {
          snap.el.setAttribute('aria-hidden', snap.prevAriaHidden)
        }
      }
      const invoker = invokerRef.current
      if (invoker !== null && document.contains(invoker)) {
        invoker.focus()
      }
      invokerRef.current = null
    }
  }, [portalHost])

  // Hardware keyboard parity with NumericKeypadSheet: digits append,
  // '.' appends, Backspace removes, Enter applies (when result is
  // valid) else closes, Escape closes, Tab cycles forward through dim
  // boxes.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        handleClose()
        return
      }
      if (e.key === 'Enter') {
        e.preventDefault()
        if (canApply) handleApply()
        else handleClose()
        return
      }
      if (e.key === 'Backspace') {
        e.preventDefault()
        backspace()
        return
      }
      if (e.key === '.') {
        e.preventDefault()
        appendDecimal()
        return
      }
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault()
        appendDigit(e.key)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [
    handleClose,
    handleApply,
    canApply,
    backspace,
    appendDecimal,
    appendDigit,
  ])

  if (portalHost === null) return null

  const handleSheetKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return
    const sheet = sheetRef.current
    if (sheet === null) return
    const focusables = getFocusableWithin(sheet)
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    if (first === undefined || last === undefined) {
      e.preventDefault()
      sheet.focus()
      return
    }
    const active = document.activeElement
    if (e.shiftKey) {
      if (active === first || !sheet.contains(active)) {
        e.preventDefault()
        last.focus()
      }
    } else if (active === last) {
      e.preventDefault()
      first.focus()
    }
  }

  const handleOverlayClick = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) handleClose()
  }

  const titleId = title !== undefined ? 'measurement-calc-title' : undefined
  const dimensionLabels = DIMENSION_LABELS_BY_COUNT[dimensions]
  const previewParts = dims.map(previewDimension).join(' × ')
  const resultPreview = result === '' ? '—' : result
  const unitSuffix = unit === '' ? '' : ` ${unit}`
  const currentLine =
    currentValue === '' ? null : `Currently: ${currentValue}${unitSuffix}`

  const dialogAriaProps: Record<string, string> =
    titleId !== undefined
      ? { 'aria-labelledby': titleId }
      : { 'aria-label': ariaLabel }

  return createPortal(
    <MeasurementCalculatorSheetContent
      sheetRef={sheetRef}
      dialogAriaProps={dialogAriaProps}
      titleId={titleId}
      title={title}
      currentLine={currentLine}
      dims={dims}
      activeIdx={activeIdx}
      dimensionLabels={dimensionLabels}
      previewParts={previewParts}
      resultPreview={resultPreview}
      unitSuffix={unitSuffix}
      canApply={canApply}
      onOverlayClick={handleOverlayClick}
      onSheetKeyDown={handleSheetKeyDown}
      onSelectDim={setActiveIdx}
      onAppendDigit={appendDigit}
      onAppendDecimal={appendDecimal}
      onBackspace={backspace}
      onClearAll={clearAll}
      onClose={handleClose}
      onApply={handleApply}
    />,
    portalHost,
  )
}
